/**
 * PatientDetailDialog Component
 * Displays patient details including triage data
 *
 * @version 2025.1.0
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  MedicalServices as MedicalIcon,
  MonitorHeart as VitalsIcon,
  Print as PrintIcon,
  Campaign as CallIcon,
  AccessTime as TimeIcon,
  ConfirmationNumber as TicketIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { ReceptionAppointment, AttendanceStatus } from '../../../types/reception';
import { TriageData, calculateBMI, getBMIClassification, formatBloodPressure } from '../../../types/triage';
import {
  APPOINTMENT_EXAM_STATUS_LABELS,
  APPOINTMENT_EXAM_STATUS_COLORS,
  APPLICATION_LEVEL_LABELS,
  APPLICATION_LEVEL_COLORS,
} from '../../../types/appointmentExam';
import type { AppointmentExamStatus } from '../../../types/appointmentExam';
import { receptionService } from '../../../services/receptionService';
import AttendanceStatusBadge from './AttendanceStatusBadge';

interface PatientDetailDialogProps {
  open: boolean;
  appointment: ReceptionAppointment | null;
  onClose: () => void;
  onCallPatient?: (appointmentId: number) => Promise<void>;
  onPrintTicket?: (appointmentId: number) => void;
}

interface VitalDisplayProps {
  label: string;
  value: string | number | null;
  unit?: string;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

const VitalDisplay: React.FC<VitalDisplayProps> = ({ label, value, unit, color = 'primary' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      textAlign: 'center',
      minWidth: 100,
      borderColor: value ? `${color}.main` : 'grey.300',
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" color={value ? `${color}.main` : 'text.disabled'}>
      {value ?? '-'}
      {value && unit && (
        <Typography component="span" variant="caption" color="text.secondary">
          {' '}{unit}
        </Typography>
      )}
    </Typography>
  </Paper>
);

export const PatientDetailDialog: React.FC<PatientDetailDialogProps> = ({
  open,
  appointment,
  onClose,
  onCallPatient,
  onPrintTicket,
}) => {
  const [triageData, setTriageData] = useState<TriageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch triage data when dialog opens
  useEffect(() => {
    const fetchTriage = async () => {
      if (!open || !appointment) return;

      setLoading(true);
      setError(null);
      try {
        const data = await receptionService.getTriage(appointment.id);
        setTriageData(data as TriageData | null);
      } catch (err) {
        console.error('Error fetching triage:', err);
        setTriageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTriage();
  }, [open, appointment]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTriageData(null);
      setError(null);
    }
  }, [open]);

  const handleCallPatient = async () => {
    if (!appointment || !onCallPatient) return;
    setCalling(true);
    setError(null);
    try {
      await onCallPatient(appointment.id);
    } catch (_err) {
      setError('Erro ao chamar paciente');
    } finally {
      setCalling(false);
    }
  };

  const handlePrintTicket = () => {
    if (!appointment || !onPrintTicket) return;
    onPrintTicket(appointment.id);
  };

  if (!appointment) return null;

  const formatTime = (hour: string | null, minute: string | null): string => {
    if (!hour) return '-';
    return `${hour.padStart(2, '0')}:${(minute || '0').padStart(2, '0')}`;
  };

  const formatQueueNumber = (num: number | null): string => {
    if (num === null) return '-';
    return String(num).padStart(3, '0');
  };

  const bmi = triageData ? calculateBMI(triageData.weight, triageData.height) : null;
  const bmiClassification = getBMIClassification(bmi);

  const canCall = appointment.attendanceStatus === AttendanceStatus.CHECKED_IN;
  const hasTriage = triageData && (
    triageData.bloodPressureSystolic ||
    triageData.heartRate ||
    triageData.temperature ||
    triageData.weight ||
    triageData.oxygenSaturation
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon color="primary" />
            <Typography variant="h6">Detalhes do Paciente</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Patient Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon color="action" fontSize="small" />
                <Typography variant="h6">{appointment.employeeName}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                CPF: {appointment.employeeCpf ?? '-'}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block">
                    Senha
                  </Typography>
                  <Chip
                    icon={<TicketIcon />}
                    label={formatQueueNumber(appointment.queueNumber)}
                    color="primary"
                    variant="outlined"
                    size="medium"
                    sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}
                  />
                </Box>
                <AttendanceStatusBadge status={appointment.attendanceStatus} />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Empresa
                  </Typography>
                  <Typography variant="body2">{appointment.companyName ?? '-'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <Box display="flex" alignItems="center" gap={1}>
                <MedicalIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tipo de Exame
                  </Typography>
                  <Typography variant="body2">{appointment.examType ?? '-'}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4
              }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TimeIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Horário Agendado
                  </Typography>
                  <Typography variant="body2">
                    {formatTime(appointment.hour, appointment.minute)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Complementary Exams */}
        {(appointment.appointmentExams?.length ?? 0) > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ScienceIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="medium">
                Exames Complementares
              </Typography>
              <Chip
                label={appointment.appointmentExams!.length}
                size="small"
                color="primary"
              />
            </Box>
            {appointment.appointmentExams!.map((exam) => (
              <Box
                key={exam.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={
                    APPOINTMENT_EXAM_STATUS_LABELS[exam.status as AppointmentExamStatus] ??
                    exam.status
                  }
                  color={
                    APPOINTMENT_EXAM_STATUS_COLORS[exam.status as AppointmentExamStatus] ??
                    'default'
                  }
                  size="small"
                  sx={{ minWidth: 85 }}
                />
                <Typography variant="body2">
                  {exam.occupationalExam?.examName ?? `Exame #${exam.occupationalExamId}`}
                </Typography>
                {exam.applicationLevel && (
                  <Chip
                    label={APPLICATION_LEVEL_LABELS[exam.applicationLevel] ?? exam.applicationLevel}
                    color={APPLICATION_LEVEL_COLORS[exam.applicationLevel] ?? 'default'}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                )}
                {exam.periodicityMonths && (
                  <Typography variant="caption" color="text.secondary">
                    (a cada {exam.periodicityMonths} meses)
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        )}

        {/* Triage Data */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <VitalsIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              Dados de Triagem
            </Typography>
            {loading && <CircularProgress size={16} />}
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : hasTriage ? (
            <>
              {/* Vital Signs Row */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 2.4
                  }}>
                  <VitalDisplay
                    label="Pressão Arterial"
                    value={formatBloodPressure(
                      triageData?.bloodPressureSystolic ?? null,
                      triageData?.bloodPressureDiastolic ?? null
                    )}
                    color="error"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 2.4
                  }}>
                  <VitalDisplay
                    label="Freq. Cardiaca"
                    value={triageData?.heartRate}
                    unit="bpm"
                    color="error"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 2.4
                  }}>
                  <VitalDisplay
                    label="Temperatura"
                    value={triageData?.temperature}
                    unit="°C"
                    color="warning"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 2.4
                  }}>
                  <VitalDisplay
                    label="SpO2"
                    value={triageData?.oxygenSaturation}
                    unit="%"
                    color="info"
                  />
                </Grid>
              </Grid>

              {/* Anthropometric Data Row */}
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <VitalDisplay
                    label="Peso"
                    value={triageData?.weight}
                    unit="kg"
                    color="secondary"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <VitalDisplay
                    label="Altura"
                    value={triageData?.height}
                    unit="cm"
                    color="secondary"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <VitalDisplay
                    label="IMC"
                    value={bmi}
                    color="secondary"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 3
                  }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      borderColor: bmi ? 'secondary.main' : 'grey.300',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      Classificacao
                    </Typography>
                    <Typography
                      variant="body2"
                      color={bmi ? 'secondary.main' : 'text.disabled'}
                      fontWeight="medium"
                    >
                      {bmiClassification}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Observations */}
              {triageData?.observations && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Observacoes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'action.hover' }}>
                    <Typography variant="body2">{triageData.observations}</Typography>
                  </Paper>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info" variant="outlined">
              Nenhuma triagem registrada para este paciente.
            </Alert>
          )}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {onPrintTicket && appointment.queueNumber && (
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintTicket}
          >
            Imprimir Senha
          </Button>
        )}
        {onCallPatient && canCall && (
          <Button
            variant="contained"
            color="warning"
            startIcon={calling ? <CircularProgress size={20} color="inherit" /> : <CallIcon />}
            onClick={handleCallPatient}
            disabled={calling}
          >
            Chamar Paciente
          </Button>
        )}
        <Box flex={1} />
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDetailDialog;
